(() => {
  'use strict';
  const tag = 'carisma-youtube-reels';
  if (customElements.get(tag)) return;

  let instagramReady;
  function loadInstagram() {
    if (window.instgrm?.Embeds?.process) return Promise.resolve();
    if (instagramReady) return instagramReady;
    instagramReady = new Promise((resolve, reject) => {
      let script = document.querySelector('script[src="https://www.instagram.com/embed.js"]');
      const created = !script;
      if (!script) { script = document.createElement('script'); script.src = 'https://www.instagram.com/embed.js'; script.async = true; }
      const finish = () => {
        clearTimeout(timeout);
        script.removeEventListener('load', success); script.removeEventListener('error', failure);
      };
      const success = () => { finish(); window.instgrm?.Embeds?.process ? resolve() : reject(new Error('Instagram unavailable')); };
      const failure = () => { finish(); if (created) script.remove(); reject(new Error('Instagram unavailable')); };
      const timeout = setTimeout(failure, 15000);
      script.addEventListener('load', success); script.addEventListener('error', failure);
      if (created) document.head.append(script);
    }).catch(error => { instagramReady = null; throw error; });
    return instagramReady;
  }

  class CarismaYoutubeRail extends HTMLElement {
    connectedCallback() {
      this.cleanup?.();
      this.abort = new AbortController();
      const on = (target, event, callback, options = {}) => target?.addEventListener(event, callback, { ...options, signal: this.abort.signal });
      this.track = this.querySelector('[data-rail-track]');
      if (!this.track) return;
      this.slides = [...this.querySelectorAll('[data-rail-slide]')];
      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');
      this.dots = this.querySelector('[data-rail-dots]');
      this.toggle = this.querySelector('[data-rail-autoplay]');
      this.stopButton = this.querySelector('[data-rail-stop]');
      this.player = this.querySelector('[data-rail-player]');
      this.largePlayer = this.dataset.largePlayer === 'true';
      this.status = this.querySelector('[data-rail-status]');
      this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.userPaused = this.dataset.autoplay === 'false' || this.reduced.matches || this.dataset.editor === 'true';
      this.hovering = false;
      this.inView = true;
      this.index = 0;
      this.slides.forEach((slide) => {
        const button = slide.querySelector('[data-rail-play]');
        const uploaded = this.parseMedia(slide.dataset.videoUrl);
        const media = uploaded?.kind === 'file' ? uploaded : this.parseMedia(slide.dataset.url);
        slide.media = media;
        const id = media?.id;
        if (!media) {
          button.disabled = true;
          button.textContent = 'Invalid video link';
          return;
        }
        if (media.kind === 'youtube' && !button.querySelector('img')) {
          const cover = document.createElement('img');
          cover.src = 'https://i.ytimg.com/vi/' + id + '/hqdefault.jpg';
          cover.alt = '';
          cover.loading = 'lazy';
          button.prepend(cover);
        }
        on(button, 'click', (event) => {
          if (Date.now() < (this.suppressClickUntil || 0)) { event.preventDefault(); return; }
          this.play(slide, id);
        });
      });
      on(this.prev, 'click', () => this.go(this.index - 1, true));
      on(this.next, 'click', () => this.go(this.index + 1, true));
      on(this.stopButton, 'click', () => this.stop(true));
      on(this.querySelector('[data-player-close]'), 'click', () => this.stop(true));
      on(this.player, 'cancel', (event) => { event.preventDefault(); this.stop(true); });
      on(this.player, 'click', (event) => { if (event.target === this.player) { const r = this.player.getBoundingClientRect(); if(event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom) this.stop(true); } });
      const shareVideo = async (share) => {
        if (!this.playing) return;
        const url = this.playing.media.source;
        const message = this.querySelector('[data-player-message]');
        try {
          if (share && navigator.share) await navigator.share({title:this.playing.dataset.title || 'Product video',url});
          else { await navigator.clipboard.writeText(url); message.textContent = 'Video link copied.'; }
        } catch (error) { if(error.name !== 'AbortError') message.textContent = 'Copy this link: ' + url; }
      };
      on(this.querySelector('[data-player-share]'), 'click', () => shareVideo(true));
      on(this.querySelector('[data-player-copy]'), 'click', () => shareVideo(false));
      on(this.player, 'keydown', (event) => { if(event.key === 'Escape') this.stop(true); });
      on(this.toggle, 'click', () => {
        this.userPaused = !this.userPaused;
        this.updateControls();
        this.schedule();
      });
      on(this.dots, 'click', (event) => {
        const dot = event.target.closest('[data-rail-dot]');
        if (dot) this.go(Number(dot.dataset.railDot), true);
      });
      on(this.track, 'keydown', (event) => {
        if (event.target !== this.track) return;
        const targets = { ArrowLeft: this.index - 1, ArrowRight: this.index + 1, Home: 0, End: this.positions.length - 1 };
        if (event.key in targets) { event.preventDefault(); this.go(targets[event.key], true); }
      });
      on(this.track, 'scroll', () => {
        cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(() => {
          const offset = Math.abs(this.track.scrollLeft);
          this.index = this.positions.reduce((best, pos, i) => Math.abs(pos - offset) < Math.abs(this.positions[best] - offset) ? i : best, 0);
          if (this.playing && !this.largePlayer) {
            const card = this.playing.getBoundingClientRect(), viewport = this.track.getBoundingClientRect();
            if (card.right <= viewport.left || card.left >= viewport.right) this.stop(false);
          }
          this.updateControls();
          this.schedule();
        });
      }, { passive: true });
      on(this, 'pointerenter', (e) => { if (e.pointerType === 'mouse') { this.hovering = true; this.schedule(); } });
      on(this, 'pointerleave', () => { this.hovering = false; this.schedule(); });
      on(this.track, 'dragstart', (event) => event.preventDefault());
      on(this.track, 'pointerdown', (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        this.drag = { id: event.pointerId, x: event.clientX, scroll: this.track.scrollLeft, moved: false };
        clearTimeout(this.timer);
      });
      on(window, 'pointermove', (event) => {
        if (!this.drag || event.pointerId !== this.drag.id) return;
        const distance = event.clientX - this.drag.x;
        if (Math.abs(distance) > 6) {
          this.drag.moved = true;
          this.track.classList.add('is-dragging');
          this.track.setPointerCapture(event.pointerId);
        }
        if (this.drag.moved) {
          event.preventDefault();
          this.track.scrollLeft = this.drag.scroll - distance;
        }
      });
      const endDrag = () => {
        if (!this.drag) return;
        if (this.drag.moved) this.suppressClickUntil = Date.now() + 350;
        if (this.track.hasPointerCapture(this.drag.id)) this.track.releasePointerCapture(this.drag.id);
        this.drag = null;
        this.track.classList.remove('is-dragging');
        this.schedule();
      };
      on(window, 'pointerup', endDrag);
      on(window, 'pointercancel', endDrag);
      on(this.track, 'lostpointercapture', endDrag);
      on(this, 'pointerdown', () => clearTimeout(this.timer));
      on(this, 'pointerup', () => this.schedule());
      on(this, 'focusin', () => this.schedule());
      on(this, 'focusout', () => { clearTimeout(this.focusTimer); this.focusTimer = setTimeout(() => this.schedule(), 0); });
      on(document, 'visibilitychange', () => this.schedule());
      on(this.reduced, 'change', () => { if (this.reduced.matches) this.userPaused = true; this.updateControls(); this.schedule(); });
      this.resizeObserver = new ResizeObserver(() => this.measure());
      this.resizeObserver.observe(this.track);
      this.intersectionObserver = new IntersectionObserver(([entry]) => { this.inView = entry.isIntersecting; if (!this.inView && !this.largePlayer) this.stop(false); this.schedule(); });
      this.intersectionObserver.observe(this);
      this.measure();

    }

    getYoutubeId(value) {
      const input = String(value || '').replace(/[\u200B-\u200D\uFEFF]/g, '').trim().replace(/&amp;/gi, '&');
      if (/^[A-Za-z0-9_-]{11}$/.test(input)) return input;
      try {
        const address = input.startsWith('//') ? `https:${input}` : (/^https?:\/\//i.test(input) ? input : `https://${input}`);
        const url = new URL(address);
        if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password) return null;
        const host = url.hostname.toLowerCase().replace(/^(www\.|m\.)/, '');
        const parts = url.pathname.split('/').filter(Boolean);
        let id;
        if (host === 'youtu.be') id = parts[0];
        else if (['youtube.com', 'youtube-nocookie.com'].includes(host)) {
          if (['shorts', 'embed', 'live', 'v'].includes(parts[0])) id = parts[1];
          else if (parts[0] === 'watch') id = url.searchParams.get('v');
        }
        return /^[A-Za-z0-9_-]{11}$/.test(id || '') ? id : null;
      } catch (_) { return null; }
    }

    parseMedia(value) {
      const input = String(value || '').replace(/[\u200B-\u200D\uFEFF]/g, '').trim().replace(/&amp;/gi, '&');
      const youtubeId = this.getYoutubeId(input);
      if (youtubeId) return { kind: 'youtube', id: youtubeId, label: 'YouTube', source: `https://www.youtube.com/watch?v=${youtubeId}` };
      if (!input || /[<>\s]/.test(input) || (/^[a-z][a-z\d+.-]*:/i.test(input) && !/^https?:/i.test(input))) return null;
      try {
        const address = input.startsWith('//') ? `https:${input}` : (/^https?:\/\//i.test(input) ? input : `https://${input}`);
        const url = new URL(address);
        if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password || !url.hostname.includes('.')) return null;
        const host = url.hostname.toLowerCase().replace(/^(www\.|m\.)/, '');
        const parts = url.pathname.split('/').filter(Boolean);
        const base = { source: url.href, label: host };
        if (/\.(mp4|webm|ogv|ogg|mov|m4v)$/i.test(url.pathname)) return { ...base, kind: 'file', label: 'Video' };
        if (host === 'instagram.com') {
          // Public permalinks may include a username before /reel/ or /p/.
          // Share redirects are not shortcodes and must remain original links.
          const offset = parts.length === 3 && /^[A-Za-z0-9_.]+$/.test(parts[0]) && !['share', 'stories', 'explore', 'accounts', 'direct'].includes(parts[0].toLowerCase()) ? 1 : 0;
          const route = parts[offset] === 'reels' ? 'reel' : parts[offset];
          const id = parts[offset + 1];
          if (parts.length === offset + 2 && ['reel', 'p', 'tv'].includes(route) && /^[A-Za-z0-9_-]+$/.test(id || '')) {
            return { kind: 'instagram', id, label: 'Instagram', source: `https://www.instagram.com/${route}/${id}/` };
          }
        }
        if (['vimeo.com', 'player.vimeo.com'].includes(host)) {
          const match = url.pathname.match(/^\/(?:video\/|channels\/[^/]+\/|groups\/[^/]+\/videos\/|showcase\/\d+\/video\/)?(\d+)(?:\/([A-Za-z0-9]+))?\/?$/);
          if (match) return { ...base, kind: 'vimeo', id: match[1], hash: url.searchParams.get('h') || match[2] || '', label: 'Vimeo' };
        }
        if (host === 'tiktok.com') {
          const match = url.pathname.match(/\/(?:@[^/]+\/video|player\/v1|embed\/v2)\/(\d+)(?:\/|$)/);
          if (match) return { ...base, kind: 'tiktok', id: match[1], label: 'TikTok' };
        }
        if (host === 'facebook.com' || host === 'web.facebook.com') {
          const match = url.pathname.match(/\/(?:reel|videos)\/(\d+)(?:\/|$)/);
          const id = match?.[1] || (['watch', 'video.php'].includes(parts[0]) ? url.searchParams.get('v') : null);
          if (/^\d+$/.test(id || '')) return { ...base, kind: 'facebook', id, label: 'Facebook', source: `https://www.facebook.com/watch/?v=${id}` };
        }
        // Short redirect links and other websites stay usable as original links.
        // Never put arbitrary customer-supplied websites inside an iframe.
        return { ...base, kind: 'external' };
      } catch (_) { return null; }
    }

    embedUrl(media, autoplay, muted) {
      const params = new URLSearchParams();
      if (media.kind === 'youtube') {
        return `https://www.youtube-nocookie.com/embed/${media.id}?${new URLSearchParams({ autoplay: autoplay ? '1' : '0', mute: muted ? '1' : '0', playsinline: '1', rel: '0' })}`;
      }
      if (media.kind === 'instagram') return `${media.source}embed/`;
      if (media.kind === 'vimeo') {
        if (media.hash) params.set('h', media.hash);
        params.set('autoplay', autoplay ? '1' : '0');
        params.set('muted', muted ? '1' : '0');
        params.set('playsinline', '1');
        return `https://player.vimeo.com/video/${media.id}?${params}`;
      }
      if (media.kind === 'tiktok') {
        // TikTok's muted=1 disables its volume control, so keep user audio controls.
        return `https://www.tiktok.com/player/v1/${media.id}?${new URLSearchParams({ autoplay: autoplay ? '1' : '0', controls: '1', rel: '0' })}`;
      }
      if (media.kind === 'facebook') {
        return `https://www.facebook.com/plugins/video.php?${new URLSearchParams({ href: media.source, show_text: 'false', autoplay: autoplay ? 'true' : 'false', mute: muted ? 'true' : 'false' })}`;
      }
      return null;
    }

    measure() {
      const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const first = this.slides[0]?.getBoundingClientRect();
      const rtl = getComputedStyle(this.track).direction === 'rtl';
      this.rtl = rtl;
      const positions = this.slides.map(slide => {
        const rect = slide.getBoundingClientRect();
        return Math.min(max, Math.max(0, rtl ? first.right - rect.right : rect.left - first.left));
      });
      this.positions = positions.filter((pos, i) => i === 0 || Math.abs(pos - positions[i - 1]) > 1);
      if (!this.positions.length) this.positions = [0];
      this.index = Math.min(this.index, this.positions.length - 1);
      if (this.dots && this.dots.children.length !== this.positions.length) {
        this.dots.replaceChildren(...this.positions.map((_, i) => {
          const dot = document.createElement('button');
          dot.type = 'button'; dot.className = 'carisma-rail__dot'; dot.dataset.railDot = i;
          dot.setAttribute('aria-label', 'Show video group ' + (i + 1));
          return dot;
        }));
      }
      this.updateControls();
      this.schedule();
    }

    go(index, announce = false) {
      const count = this.positions.length;
      if (count < 2) return;
      if (this.dataset.loop === 'true') index = (index + count) % count;
      else index = Math.max(0, Math.min(index, count - 1));
      if (!this.largePlayer) this.stop(false);
      this.index = index;
      this.track.scrollTo({ left: this.positions[index] * (this.rtl ? -1 : 1), behavior: this.reduced.matches ? 'auto' : 'smooth' });
      this.updateControls();
      if (announce && this.status) this.status.textContent = 'Video group ' + (index + 1) + ' of ' + count;
      this.schedule();
    }

    play(slide, id, userInitiated = true) {
      this.stop(false);
      clearTimeout(this.timer);
      this.playing = slide;
      const media = slide.media || this.parseMedia(slide.dataset.url);
      if (!media) return;
      let iframe;
      if (media.kind === 'file') {
        iframe = document.createElement('video');
        iframe.controls = true; iframe.playsInline = true; iframe.preload = 'metadata';
        iframe.muted = this.dataset.muted === 'true';
        iframe.src = media.source;
        if (userInitiated) iframe.play()?.catch(() => {});
      } else if (media.kind === 'instagram') {
        iframe = document.createElement('div');
        iframe.className = 'carisma-rail__instagram';
        const quote = document.createElement('blockquote');
        quote.className = 'instagram-media';
        quote.setAttribute('data-instgrm-permalink', media.source);
        quote.setAttribute('data-instgrm-version', '14');
        const link = document.createElement('a');
        link.href = media.source; link.target = '_blank'; link.rel = 'noopener noreferrer';
        link.textContent = 'View this reel on Instagram';
        quote.append(link); iframe.append(quote);
      } else if (media.kind === 'external') {
        iframe = document.createElement('a');
        iframe.href = media.source; iframe.target = '_blank'; iframe.rel = 'noopener noreferrer';
        iframe.textContent = 'Open original video';
      } else {
        iframe = document.createElement('iframe');
        iframe.src = this.embedUrl(media, userInitiated, this.dataset.muted === 'true');
        iframe.title = slide.dataset.title || media.label + ' video';
        iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
        iframe.allowFullscreen = true;
        iframe.referrerPolicy = 'strict-origin-when-cross-origin';
      }
      const sourceLink = this.querySelector('[data-player-source]');
      if (sourceLink) sourceLink.href = this.parseMedia(slide.dataset.url)?.source || media.source;
      slide.classList.add('is-selected');
      slide.querySelector('[data-rail-play]').setAttribute('aria-pressed', 'true');
      if (this.largePlayer && this.player) {
        this.player.hidden = false;
        this.player.querySelector('[data-player-title]').textContent = slide.dataset.title || '';
        this.player.querySelector('[data-player-subtitle]').textContent = slide.dataset.subtitle || '';
        this.player.querySelector('[data-player-message]').textContent = '';
        this.player.querySelector('[data-player-media]').dataset.provider = media.kind;
        this.player.querySelector('[data-player-media]').replaceChildren(iframe);

      } else {
        slide.querySelector('[data-rail-play]').hidden = true;
        slide.querySelector('[data-rail-media]').append(iframe);
        this.stopButton.hidden = false;
      }
      if (media.kind === 'instagram') {
        loadInstagram().then(() => {
          if (this.isConnected && this.playing === slide && iframe.isConnected) window.instgrm.Embeds.process();
        }).catch(() => {
          if (this.playing === slide && iframe.isConnected) {
            const message = this.querySelector('[data-player-message]');
            if (message) message.textContent = 'Instagram could not load here. Use Open original video below.';
          }
        });
      }
      if (userInitiated) {
        this.player?.scrollIntoView({ behavior: this.reduced.matches ? 'instant' : 'smooth', block: 'nearest' });
        this.querySelector('[data-player-close]')?.focus({ preventScroll: true });
      }
    }

    stop(restoreFocus) {
      if (!this.playing) return;
      const button = this.playing.querySelector('[data-rail-play]');
      this.querySelectorAll('video').forEach(video => { video.pause(); video.removeAttribute('src'); video.load(); video.remove(); });
      this.playing.querySelector('iframe')?.remove();
      this.playing.querySelector('.carisma-rail__instagram')?.remove();
      this.player?.querySelector('[data-player-media]')?.replaceChildren();
      if (this.player) { if (this.player.open) this.player.close(); this.player.hidden = true; }
      this.playing.classList.remove('is-selected');
      button.setAttribute('aria-pressed', 'false');
      button.hidden = false;
      this.playing = null;
      if (this.stopButton) this.stopButton.hidden = true;
      if (restoreFocus) button.focus({ preventScroll: true });
      this.schedule();
    }

    updateControls() {
      const count = this.positions.length, loop = this.dataset.loop === 'true';
      if (this.prev) this.prev.disabled = count < 2 || (!loop && this.index === 0);
      if (this.next) this.next.disabled = count < 2 || (!loop && this.index === count - 1);
      if (this.dots) {
        this.dots.hidden = count < 2;
        [...this.dots.children].forEach((dot, i) => dot.setAttribute('aria-current', String(i === this.index)));
      }
      if (this.toggle) { this.toggle.hidden = count < 2; this.toggle.textContent = this.userPaused ? 'Start auto-scroll' : 'Pause auto-scroll'; this.toggle.setAttribute('aria-pressed', String(!this.userPaused)); }
    }

    schedule() {
      clearTimeout(this.timer);
      if (!this.isConnected || this.drag || this.dataset.editor === 'true' || this.userPaused || this.hovering || !this.inView || this.playing || document.hidden || (this.contains(document.activeElement) && document.activeElement !== this.toggle) || (this.positions?.length || 0) < 2) return;
      if (this.dataset.loop !== 'true' && this.index === this.positions.length - 1) return;
      this.timer = setTimeout(() => this.go(this.index + 1), Math.max(3, Number(this.dataset.interval) || 5) * 1000);
    }

    cleanup() {
      this.drag = null;
      this.track?.classList?.remove('is-dragging');
      this.abort?.abort();
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
      clearTimeout(this.timer); clearTimeout(this.focusTimer); cancelAnimationFrame(this.scrollFrame);
      this.stop(false);
      clearTimeout(this.timer);
    }
    disconnectedCallback() { this.cleanup(); }
  }
  customElements.define(tag, CarismaYoutubeRail);
})();
